// SOURCE OF TRUTH: poweryourflow-ops — do not edit here unless this file is
// poweryourflow-ops/docs/schema/types.ts. Every other copy is byte-identical:
//   poweryourflow-functions/src/schema/types.ts
//   poweryourflow-app/src/schema/types.ts
//   poweryourflow-lib/src/schema/types.ts
// Run scripts/check-schema.sh after any change.
//
// Rules that every type below follows:
//   - timestamps are epoch ms `number`, never a database Timestamp
//   - changes are additive: new fields optional, no rename, no type change
//   - a constant that states a promise (a limit, a price, a legal rule)
//     lives here and has a test in the functions repo

/** Bumped on every change to this file. The functions repo asserts it. */
export const SCHEMA_VERSION = 10;

/** Epoch milliseconds. */
export type EpochMs = number;

/**
 * Bounds that every loop over data respects. A loop over data with no
 * bound is a bill on a serverless runtime; the bound is a constant so a
 * test can assert it.
 */
export const LIMITS = {
  /** Documents processed per scheduled run; the rest waits for the next run. */
  SWEEP_BATCH: 200,
  /** Retries of an outbound call before giving up. */
  MAX_RETRIES: 3,
  /** Questions in one questionnaire, screens and complementary fields together. */
  MAX_QUESTIONS: 60,
  /** Options of one question. */
  MAX_OPTIONS: 20,
  /** Characters in any free-text answer; a question may set a lower cap. */
  TEXT_ANSWER_MAX: 200,
  /** Characters in a first name, here as in account creation. */
  FIRST_NAME_MAX: 40,
  /** Characters in the other written answers of Dados gerais. */
  PROFILE_TEXT_MAX: 60,
  /** Youngest age accepted at sign-up and in a first Dados gerais she starts alone. */
  MIN_AGE_YEARS: 14,
  /**
   * Youngest age of an account the team creates (SPEC-54), and of the birth
   * date Dados gerais accepts for such an account: the server knows one by
   * the `createdByTeam` custom claim it sets at its creation, never from
   * the request. Every other account keeps MIN_AGE_YEARS.
   */
  TEAM_MIN_AGE_YEARS: 8,
  /** Oldest age Dados gerais accepts. */
  MAX_AGE_YEARS: 80,
  /** Upper end of the "treinos por semana" stepper. */
  TRAININGS_PER_WEEK_MAX: 21,
  /** Upper end of the "horas por semana" stepper. */
  HOURS_PER_WEEK_MAX: 60,
  /** Versions one assessment can reach through redos; a guard, not a product limit. */
  MAX_RUN_VERSION: 1000,
  /** Accounts one read of the admin's Base de utilizadoras covers; past it the read is refused, never cut short. */
  ADMIN_ACCOUNTS_MAX: 2000,
  /** Bilans presenciais one athlete can hold; a new one past it is refused, and a profile read never cuts the list short. */
  BILANS_PER_ACCOUNT_MAX: 200,
  /** Characters in the clinician's note on one section of a bilan. */
  BILAN_NOTE_MAX: 2000,
  /** « Duração do ciclo » of a bilan's cycle mark, in days (SPEC-59). */
  CYCLE_LENGTH_MIN: 20,
  CYCLE_LENGTH_MAX: 45,
  /** « Duração do período », in days; never more than the cycle's length less CYCLE_PERIOD_GAP_DAYS. */
  PERIOD_LENGTH_MIN: 1,
  PERIOD_LENGTH_MAX: 10,
  /** Days a period leaves free at the end of its cycle, so it never reaches the estimated ovulation's three days. */
  CYCLE_PERIOD_GAP_DAYS: 16,
  /** How far back the first day of her last period may be: this many months before the session. */
  CYCLE_FIRST_DAY_MONTHS: 12,
} as const;

// ---------------------------------------------------------------------------
// Assessments (SPEC-25)
//
// Firestore layout, built in poweryourflow-functions/src/paths.ts:
//   accounts/{accountId}/profile/current         Profile
//   accounts/{accountId}/runs/{assessmentId}     AssessmentRun, current version
//   archive/{accountId}/runs/{assessmentId}-v{n} AssessmentRun, older versions,
//                                                server-side only
// ---------------------------------------------------------------------------

/** The nine assessments, in the hub's canonical order. */
export const ASSESSMENT_IDS = [
  "dados",
  "iciq",
  "ciclo-sintomas",
  "ciclo-desporto",
  "leafq",
  "sono",
  "beda",
  "ciss",
  "fonseca",
] as const;

export type AssessmentId = (typeof ASSESSMENT_IDS)[number];

/**
 * todo: the run exists with no answer (a redo just started).
 * progress: at least one answer, not finished.
 * done: finished; the run is frozen.
 * An assessment with no run document at all is todo too.
 */
export type RunState = "todo" | "progress" | "done";

/**
 * One answer. A choice is its option id, a multiple choice the list of
 * option ids, a scale or a count a number, a written answer a string.
 */
export type AnswerValue = string | number | boolean | readonly string[];

/** Answers of one run, by question id. */
export type Answers = Readonly<Record<string, AnswerValue>>;

/** One version of one assessment, for one athlete. */
export interface AssessmentRun {
  accountId: string;
  assessmentId: AssessmentId;
  /** 1 for the first run; a redo adds 1. */
  version: number;
  state: RunState;
  answers: Answers;
  /** Visible questions with an answer. */
  answeredCount: number;
  /** Questions the athlete may still see, given the answers so far. */
  visibleTotal: number;
  /** Set on finish for an assessment that has a score (ICIQ 0–21, Ciclo · sintomas 0–12, ASBQ 18–90, CISS 0–60, Fonseca 0–100). */
  score?: number;
  startedAt: EpochMs;
  finishedAt?: EpochMs;
  /** finishedAt of the version this redo replaces; the hub shows it until the redo finishes. */
  previousFinishedAt?: EpochMs;
  /** The score of the version this redo replaces, for an assessment that has one; its alert holds until the redo finishes. */
  previousScore?: number;
  /** The device that last changed the answers. */
  deviceId: string;
  /** Device clock at the last answer; breaks a tie between two devices. */
  answeredAt: EpochMs;
  /** Server clock at the last write. */
  updatedAt: EpochMs;
}

export type CompetitionType = "league" | "events";

/** Dados gerais, as the hub and Perfil read it. */
export interface Profile {
  accountId: string;
  firstName?: string;
  /** A calendar date, YYYY-MM-DD, not an instant: a birthday has no time zone. */
  birthDate?: string;
  modality?: string;
  position?: string;
  level?: string;
  /** null means "Sem clube". */
  club?: string | null;
  trainingsPerWeek?: number;
  hoursPerWeek?: number;
  competition?: CompetitionType;
  /** Her strong side, in her sport's words (SPEC-55). */
  laterality?: Laterality;
  updatedAt: EpochMs;
}

/**
 * Which question asked her side (SPEC-55): her kicking foot, take-off leg,
 * guard, throwing hand, standing leg, or plain handedness. Chosen from her
 * sport in poweryourflow-lib's laterality.ts.
 */
export const LATERALITY_KINDS = ["pe", "impulsao", "guarda", "mao", "apoio", "geral"] as const;

export type LateralityKind = (typeof LATERALITY_KINDS)[number];

export interface Laterality {
  kind: LateralityKind;
  /** An option id of that kind's list, or her own words. */
  value: string;
}

/** The fields an athlete writes; the server adds accountId and updatedAt. */
export type ProfileFields = Omit<Profile, "accountId" | "updatedAt">;

// Callable functions, request and response. Identity comes from the
// accountId custom claim, never from the request.

/** The whole answer set of a run, as the device holds it. Idempotent. */
export interface SaveAnswersRequest {
  assessmentId: AssessmentId;
  version: number;
  answers: Answers;
  deviceId: string;
  answeredAt: EpochMs;
}

/** Save, then freeze the run. finishedAt is the device clock at "Terminar". */
export interface FinishRunRequest extends SaveAnswersRequest {
  finishedAt: EpochMs;
}

/** Start version fromVersion + 1 of a finished assessment. */
export interface StartRedoRequest {
  assessmentId: AssessmentId;
  fromVersion: number;
}

export interface SaveProfileRequest {
  profile: ProfileFields;
}

export interface RunResponse {
  run: AssessmentRun;
}

export interface ProfileResponse {
  profile: Profile;
}

// ---------------------------------------------------------------------------
// Settings (SPEC-31)
//
//   accounts/{accountId}/settings/current        AccountSettings
//
// The athlete's own choices about the app, apart from Dados gerais: they
// are no health data and no assessment reads them.
// ---------------------------------------------------------------------------

/** The app's languages; PT is the reference every text is written in first. */
export const LANGUAGES = ["pt", "fr", "en"] as const;

export type Language = (typeof LANGUAGES)[number];

export interface AccountSettings {
  accountId: string;
  /** The app's language. On another device, the account's language wins over the device's. */
  language: Language;
  updatedAt: EpochMs;
}

export interface SaveSettingsRequest {
  language: Language;
}

export interface SettingsResponse {
  settings: AccountSettings;
}

// ---------------------------------------------------------------------------
// Admin (SPEC-33)
//
// The PYF team's view of every account, read-only until the bilan
// presencial and the athlete's language (SPEC-44). A team account carries
// the `team: true` custom claim, which mainBrain's "Grant team access"
// workflow sets after the founder approves the run; the app never sets
// it. The admin callables check it, write the access log, and compute
// every number below; firestore.rules do not change.
//
//   adminLog/{entryId}    AdminLogEntry, server-side only
// ---------------------------------------------------------------------------

/**
 * Where an account stands, the first that applies: a result at or above
 * its threshold, no activity for 30 days, an assessment started and not
 * finished, every assessment done, otherwise active.
 */
export type AdminUserStatus = "alert" | "inactive" | "progress" | "complete" | "active";

/** One row of Base de utilizadoras. */
export interface AdminUserRow {
  accountId: string;
  email: string;
  firstName?: string;
  /** null means "Sem clube"; absent means Dados gerais never answered it. */
  club?: string | null;
  modality?: string;
  level?: string;
  /** Whole years on the day of the read; absent without a birth date. */
  age?: number;
  /** Assessments done, as the athlete's hub counts them. */
  done: number;
  /** Assessments started and not finished. */
  inProgress: number;
  /** Assessments available: ASSESSMENT_IDS.length. */
  total: number;
  createdAt: EpochMs;
  /** The latest of a sign-in or session refresh, an answer saved, a Dados gerais save. */
  lastActivityAt: EpochMs;
  /** At least one result at or above its threshold. */
  alert: boolean;
  /** No activity for 30 days. */
  inactive: boolean;
  status: AdminUserStatus;
}

/** The four counters above the table, over every account whatever the filter. */
export interface AdminStats {
  accounts: number;
  /** Accounts created in the last 30 days. */
  newAccounts: number;
  /** Assessments done, every account together. */
  done: number;
  /** accounts × ASSESSMENT_IDS.length. */
  possible: number;
  /** done over possible, a whole percent; 0 when possible is 0. */
  donePercent: number;
  /** Assessments started and not finished, every account together. */
  inProgress: number;
  /** Accounts with an alert. */
  alerts: number;
}

export interface AdminUsersResponse {
  stats: AdminStats;
  /** Latest activity first. */
  rows: AdminUserRow[];
  /** Server clock at the read. */
  readAt: EpochMs;
}

/** The whole base as a file, whatever the screen filters. */
export interface AdminExportResponse {
  /** pyf-utilizadoras-YYYY-MM-DD.csv */
  filename: string;
  /** ";" separated, CRLF lines, a UTF-8 byte order mark first. */
  content: string;
}

/** A finished assessment's score against its clinical threshold (SPEC-34). */
export interface AdminResult {
  score: number;
  /** The instrument's highest score: 21, 12, 90, 60 or 100. */
  max: number;
  /** The score from which a result raises an alert; absent while none is set. */
  threshold?: number;
  /** score at or above threshold. */
  alert: boolean;
}

/** One assessment on an athlete's profile (SPEC-34), in ASSESSMENT_IDS order. */
export interface AdminAssessment {
  assessmentId: AssessmentId;
  /** Where the athlete's hub places it: a redo in progress stays done. */
  group: "todo" | "progress" | "done";
  /** Done: the day the version shown was finished. */
  doneAt?: EpochMs;
  /** In progress: screens answered, and the screens she may still see. */
  answered?: number;
  visibleTotal?: number;
  /** Done, for an assessment that has a score. */
  result?: AdminResult;
  /** Done: the answers of the version shown, the last she finished. */
  answers?: Answers;
}

/** One athlete's profile and results, for the team (SPEC-34). */
export interface AdminProfileResponse {
  accountId: string;
  email: string;
  createdAt: EpochMs;
  /** As on her row of the base. */
  lastActivityAt: EpochMs;
  status: AdminUserStatus;
  profile: Profile | null;
  /** Whole years on the day of the read; absent without a birth date. */
  age?: number;
  done: number;
  inProgress: number;
  total: number;
  /** The assessments whose result reached its threshold, in ASSESSMENT_IDS order. */
  alerts: AssessmentId[];
  /** Calendar days, UTC, from the account's creation and from its last activity to the read. */
  daysSinceCreated: number;
  daysSinceActivity: number;
  assessments: AdminAssessment[];
  /** Server clock at the read. */
  readAt: EpochMs;
  /** Her bilans presenciais (SPEC-45), newest session first; absent from a server older than schema 8. */
  bilans?: Bilan[];
  /** Her app's language (SPEC-52); absent while she never chose one, which the app reads as PT. */
  language?: Language;
}

/** Why the team reads one athlete: to show her profile, or to export her answers. Each is logged as itself. */
export type AdminProfilePurpose = "view" | "export";

export interface AdminProfileRequest {
  accountId: string;
  purpose: AdminProfilePurpose;
}

/**
 * What a team account read or changed: the base, its export, one
 * athlete's profile, her answers as a file; since SPEC-44 a bilan
 * created, a section of it saved, its state changed, its file, and her
 * app's language set; since SPEC-54 an account created by hand; since
 * SPEC-62 a bilan's cycle mark recorded.
 */
export type AdminAction =
  | "list"
  | "export"
  | "profile"
  | "answers-export"
  | "bilan-create"
  | "bilan-save"
  | "bilan-state"
  | "bilan-export"
  | "bilan-cycle"
  | "language"
  | "account-create";

/** One line of the access log, written before the read or the write it records. */
export interface AdminLogEntry {
  action: AdminAction;
  /** The team account's uid. */
  actorId: string;
  /** The athlete's account, for anything but the base. */
  accountId?: string;
  /** The bilan, for a bilan action other than its creation. */
  bilanId?: string;
  at: EpochMs;
}

/**
 * Asks an admin callable to start, and nothing more (SPEC-43). The admin
 * sends it to adminUsers, adminAccount and adminExport as it opens, before
 * it knows who is signed in, so an instance is up when the first real call
 * arrives. It needs no claim, reads nothing and logs nothing.
 */
export interface AdminWarmUpRequest {
  warm: true;
}

/** The answer to an AdminWarmUpRequest: nothing. */
export type AdminWarmUpResponse = Record<string, never>;

// ---------------------------------------------------------------------------
// Bilan presencial (SPEC-44 to SPEC-51)
//
// The clinical team's in-person assessment of one athlete, filled in during
// a session in the admin and never shown to her. The protocol (its blocks,
// sections, fields, body zones and lesions, with their PT words) lives in
// poweryourflow-lib's bilan.ts. A stored value is an id from it, never a
// label, so the words can change without touching a stored bilan.
//
//   bilans/{accountId}/items/{bilanId}    Bilan, server-side only
//
// No client reads bilans/: the athlete's subtree under accounts/ stays
// hers, and the team reads and writes through the admin callables, which
// check the team claim and log every action in adminLog first.
// ---------------------------------------------------------------------------

/** The fourteen sections of the PYF protocol, in its fixed order (SPEC-51). */
export const BILAN_SECTION_IDS = [
  "antecedentes",
  "dores",
  "medico",
  "preferencias",
  "captores",
  "osteopatico",
  "estatico",
  "dinamico",
  "complementar",
  "marcha",
  "corrida",
  "pelvica",
  "escapular",
  "diafragma",
] as const;

export type BilanSectionId = (typeof BILAN_SECTION_IDS)[number];

/** progress: « Em curso », editable. saved: « Guardado », locked until « Editar » reopens it. */
export type BilanState = "progress" | "saved";

/** One zone of 1.3 Antecedentes de lesão with what is marked on it (SPEC-46). */
export interface BilanInjury {
  /** A zone id, with its side for a paired zone: « joelho-e », « lombares ». */
  zone: string;
  /** Lesion ids from the zone's list, in the order they were marked. */
  lesions: string[];
  /** « Outra lesão nesta zona », as typed; absent when empty. */
  other?: string;
}

export interface Bilan {
  bilanId: string;
  accountId: string;
  /** The session's day, YYYY-MM-DD, on the clinician's device. */
  date: string;
  /** The team account that created it. */
  clinicianId: string;
  /** How the list names that account: its display name, else its email. */
  clinicianName: string;
  state: BilanState;
  /** 1.3: the zones with something marked, in the order they were first marked. */
  injuries: BilanInjury[];
  /** Every other section's answers, by section id; a section never touched is absent. */
  answers: Partial<Record<BilanSectionId, Answers>>;
  /** The clinician's note on each section; an empty note is absent. */
  notes: Partial<Record<BilanSectionId, string>>;
  /** « Momento do ciclo » (SPEC-62): absent until the team records it, and on every bilan made before schema 10. */
  cycle?: BilanCycle;
  createdAt: EpochMs;
  updatedAt: EpochMs;
}

/** « Novo bilan »: a bilan dated today on the clinician's device. */
export interface AdminBilanCreate {
  action: "create";
  accountId: string;
  date: string;
}

/**
 * One section of a bilan, as the screen holds it now: each part sent
 * replaces that part whole (1.3's injuries, another section's answers,
 * the section's note), so the same request twice leaves the same bilan.
 */
export interface AdminBilanSave {
  action: "save";
  accountId: string;
  bilanId: string;
  sectionId: BilanSectionId;
  injuries?: BilanInjury[];
  answers?: Answers;
  note?: string;
}

/** « Guardar bilan » (saved) or « Editar » (progress). */
export interface AdminBilanSetState {
  action: "state";
  accountId: string;
  bilanId: string;
  state: BilanState;
}

/** « Exportar »: the bilan as a CSV file, logged as an export. */
export interface AdminBilanExport {
  action: "export";
  accountId: string;
  bilanId: string;
}

/**
 * « Guardar » in « Momento do ciclo » (SPEC-62): the mark replaces the
 * bilan's whole, while the bilan is in progress. The server computes a
 * natural cycle's day and phase and ignores any it is sent.
 */
export interface AdminBilanSetCycle {
  action: "cycle";
  accountId: string;
  bilanId: string;
  cycle: BilanCycleEntry;
}

export type AdminBilanRequest = AdminBilanCreate | AdminBilanSave | AdminBilanSetState | AdminBilanExport | AdminBilanSetCycle;

/** The bilan as stored after the action. */
export interface AdminBilanResponse {
  bilan: Bilan;
  /** export only: pyf-{accountId}-bilan-{date}.csv, ";" separated, CRLF lines, a UTF-8 byte order mark first. */
  file?: AdminExportResponse;
}

// ---------------------------------------------------------------------------
// Momento do ciclo (SPEC-57 to SPEC-62)
//
// Where the athlete stands in her cycle on the session's day, as the team
// notes it on a bilan presencial: one mark at most, on the Bilan, absent
// until it is filled. Like the rest of a bilan it is stored as ids, never
// words, and no client reads it. For a natural cycle the server computes the
// day and the phase from the bilan's date with the one rule in
// poweryourflow-lib's cycle.ts, the rule the admin's panel previews with.
// ---------------------------------------------------------------------------

/** natural « Ciclo natural », hormonal « Contraceção hormonal », none « Sem período », unknown « Não sabe ou prefere não dizer ». */
export const CYCLE_SITUATIONS = ["natural", "hormonal", "none", "unknown"] as const;
export type CycleSituation = (typeof CYCLE_SITUATIONS)[number];

/** « Ciclo regular? »: Sim, Não, Não sabe. */
export const CYCLE_REGULARITIES = ["sim", "nao", "nao-sabe"] as const;
export type CycleRegularity = (typeof CYCLE_REGULARITIES)[number];

/** A natural cycle's phase on the session's day; alem: past her usual length (« Para lá do ciclo habitual »). */
export const CYCLE_PHASES = ["menstrual", "folicular", "ovulacao", "lutea", "alem"] as const;
export type CyclePhase = (typeof CYCLE_PHASES)[number];

/** « Método », the LEAF-Q's methods with the pill first: pílula, anel, DIU hormonal, implante, adesivo, outro. */
export const CONTRACEPTION_METHODS = ["pilula", "anel", "diu", "implante", "adesivo", "outro"] as const;
export type ContraceptionMethod = (typeof CONTRACEPTION_METHODS)[number];

/** « Neste momento », for the pill: comprimidos ativos, pausa ou placebo, não sabe. */
export const PILL_MOMENTS = ["ativos", "pausa", "nao-sabe"] as const;
export type PillMoment = (typeof PILL_MOMENTS)[number];

/** « Há quanto tempo? » without a period: menos de 3 meses, 3 a 6 meses, mais de 6 meses, ainda não teve o primeiro. */
export const NO_PERIOD_SINCE = ["menos-3-meses", "3-6-meses", "mais-6-meses", "nunca-teve"] as const;
export type NoPeriodSince = (typeof NO_PERIOD_SINCE)[number];

export interface NaturalCycleEntry {
  situation: "natural";
  /** The first day of her last period, YYYY-MM-DD: from CYCLE_FIRST_DAY_MONTHS before the session to its day. */
  firstDay: string;
  /** Days, LIMITS.CYCLE_LENGTH_MIN to CYCLE_LENGTH_MAX. */
  cycleLength: number;
  /** Days, LIMITS.PERIOD_LENGTH_MIN to PERIOD_LENGTH_MAX, and at most cycleLength less CYCLE_PERIOD_GAP_DAYS. */
  periodLength: number;
  regular: CycleRegularity;
}

export interface HormonalCycleEntry {
  situation: "hormonal";
  method: ContraceptionMethod;
  /** Required with the pill, absent with any other method. */
  pillMoment?: PillMoment;
}

export interface NoPeriodCycleEntry {
  situation: "none";
  since: NoPeriodSince;
}

export interface UnknownCycleEntry {
  situation: "unknown";
}

/** What the clinician records in « Momento do ciclo »: one situation and its fields, nothing of another. */
export type BilanCycleEntry = NaturalCycleEntry | HormonalCycleEntry | NoPeriodCycleEntry | UnknownCycleEntry;

/** Who recorded a cycle mark, and when. */
export interface BilanCycleRecord {
  /** The team account's uid. */
  recordedBy: string;
  recordedAt: EpochMs;
}

/** A natural cycle as stored: the server's day and phase on the session's day. */
export interface NaturalCycle extends NaturalCycleEntry {
  /** D: days from firstDay to the bilan's date, plus one. */
  day: number;
  phase: CyclePhase;
}

/** The cycle mark as a bilan holds it. */
export type BilanCycle = (NaturalCycle | HormonalCycleEntry | NoPeriodCycleEntry | UnknownCycleEntry) & BilanCycleRecord;

/** Sets an athlete's app language from her profile (SPEC-52); the app reads it at its next opening. */
export interface AdminLanguageRequest {
  accountId: string;
  language: Language;
}

export interface AdminLanguageResponse {
  settings: AccountSettings;
}

// ---------------------------------------------------------------------------
// An account created by the team (SPEC-54)
//
// For an athlete seen in consultation who has not opened the app yet. The
// server creates her Firebase Auth user with no password, sets her
// accountId claim and `createdByTeam: true`, writes her Dados gerais into
// her profile (no assessment is done) and her app's language, and logs the
// creation. The admin then sends her Firebase's set-a-password email in
// that language.
// ---------------------------------------------------------------------------

export interface AdminCreateAccountRequest {
  /** Her name as the team writes it; it becomes her profile's first name. */
  name: string;
  /** Stored in lower case; an account with this address already existing is refused. */
  email: string;
  /** YYYY-MM-DD, an age from LIMITS.TEAM_MIN_AGE_YEARS to LIMITS.MAX_AGE_YEARS. */
  birthDate: string;
  language: Language;
  modality?: string;
  position?: string;
  /** null means "Sem clube". */
  club?: string | null;
  level?: string;
  laterality?: Laterality;
}

export interface AdminCreateAccountResponse {
  accountId: string;
}
